import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, CardContent, Container, Grid, Typography, makeStyles, IconButton } from '@material-ui/core';
import { Api, DateRangeSelectRow, formatTRYSetting, InfoCard, showToast, ConfirmDialog } from 'core';
import {
  MonetizationOnOutlined,
  Payment,
  List,
  PersonAdd,
  People,
  AccountBalance,
  TrendingUp,
  ExitToApp,
  Send as SendIcon,
} from '@material-ui/icons';
import { HideableLoadingSpinnerBox } from 'reactmuicommon';
import moment from 'moment';
import { useHistory } from 'react-router';
import { logout } from 'apps/auth/store';
import { HomeStatistics } from '../models';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
    paddingTop: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 1),
      paddingTop: theme.spacing(8),
    },
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #6c9c28 0%, #65a30d 100%)',
    border: '1px solid #84cc16',
    borderRadius: 16,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 20px 40px rgba(108, 156, 40, 0.25)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      transition: 'left 0.5s',
    },
    '&:hover::before': {
      left: '100%',
    },
  },
  primaryButtonContent: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: 'white',
    position: 'relative',
    zIndex: 1,
    '&:last-child': {
      paddingBottom: theme.spacing(4),
    },
  },
  primaryIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },
  primaryTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontSize: '1.5rem',
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  primaryDescription: {
    opacity: 0.95,
    fontSize: '1rem',
  },
  actionCard: {
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 25px rgba(108, 156, 40, 0.15)',
      borderColor: '#6c9c28',
    },
  },
  actionCardContent: {
    padding: theme.spacing(3),
    textAlign: 'center',
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
  },
  actionIcon: {
    fontSize: 32,
    color: '#6c9c28',
    marginBottom: theme.spacing(1),
  },
  actionTitle: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  actionDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  statsContainer: {
    marginTop: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(4),
    },
  },
  sectionTitle: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    fontSize: '1.5rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
      marginBottom: theme.spacing(2),
    },
  },
  dateRangeContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 1px 3px rgba(108, 156, 40, 0.1)',
  },
}));

const HomePage: React.FC = () => {
  const classes = useStyles();
  const [statistics, setStatistics] = React.useState<HomeStatistics>();
  const [showLoading, setShowLoading] = React.useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);
  const history = useHistory();
  const date = new Date();

  const { userInfo } = useSelector((state: any) => ({
    userInfo: state.auth.userInfo,
  }));
  const [selectionRange, setSelectionRange] = React.useState({
    startDate: new Date(date.getFullYear(), date.getMonth(), 1),
    endDate: new Date(date.getFullYear(), date.getMonth() + 1, 0),
    key: 'selection',
  });

  const dispatch = useDispatch();

  const getHomeStatistics = async () => {
    setShowLoading(true);
    const response = await Api.Home.getHomeStatistics(
      moment(selectionRange.startDate).local().format('YYYY-MM-DD'),
      moment(selectionRange.endDate).local().format('YYYY-MM-DD'),
    );
    if (response.isSuccess) {
      setStatistics(response.result);
    } else {
      dispatch(
        showToast({
          message: `Bir hata oluştu. ${response.errorMessage}`,
          severity: 'error',
        }),
      );
    }
    setShowLoading(false);
  };

  React.useEffect(() => {
    getHomeStatistics();
  }, [selectionRange]);

  const getActionCards = () => {
    const cards = [];

    if (userInfo.isAdmin) {
      cards.push(
        {
          title: 'Ödeme Listesi',
          description: 'Tüm ödemeleri görüntüle ve yönet',
          icon: <List className={classes.actionIcon} />,
          onClick: () => history.push('/admin/payment/list'),
        },
        {
          title: 'İptal/İade Onayları',
          description: 'Bekleyen onayları incele',
          icon: <AccountBalance className={classes.actionIcon} />,
          onClick: () => history.push('/admin/payment/waitingRefundApproval/list'),
        },
        {
          title: 'Kullanıcı Oluştur',
          description: 'Yeni kullanıcı hesabı oluştur',
          icon: <PersonAdd className={classes.actionIcon} />,
          onClick: () => history.push('/admin/user/register'),
        },
        {
          title: 'Kullanıcı Listesi',
          description: 'Mevcut kullanıcıları yönet',
          icon: <People className={classes.actionIcon} />,
          onClick: () => history.push('/admin/users'),
        },
      );
    }

    if (userInfo.isAccounting) {
      cards.push({
        title: 'Ödeme Listesi',
        description: 'Tüm ödemeleri görüntüle',
        icon: <List className={classes.actionIcon} />,
        onClick: () => history.push('/admin/payment/list'),
      });
    }

    cards.push({
      title: 'Ödemelerim',
      description: 'Ödeme geçmişini görüntüle',
      icon: <TrendingUp className={classes.actionIcon} />,
      onClick: () => history.push('/paynet/payment/list/'),
    });

    // cards.push({
    //   title: 'Müşteri Ödeme Linki Oluştur',
    //   description: 'Müşterileriniz için ödeme linki oluşturun',
    //   icon: <SendIcon className={classes.actionIcon} />,
    //   onClick: () => history.push('/paynet/payment-link'),
    // });

    return cards;

  };

  if (showLoading) return <HideableLoadingSpinnerBox />;

  return (
    <Container>
      <ConfirmDialog
        title="Çıkış"
        open={showLogoutDialog}
        handleConfirm={() => dispatch(logout())}
        handleClose={() => setShowLogoutDialog(false)}
      >
        Çıkış yapmak istiyor musunuz ?
      </ConfirmDialog>

      <IconButton
        color="primary"
        style={{
          position: 'fixed',
          top: 80,
          right: 20,
          zIndex: 9999,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
        onClick={() => setShowLogoutDialog(true)}
      >
        <ExitToApp />
      </IconButton>

      <Box className={classes.root}>
        {/* Ödeme Al section at the top */}
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Card className={classes.primaryButton} onClick={() => history.push('/paynet/payment/')}>
              <CardContent className={classes.primaryButtonContent}>
                <Payment className={classes.primaryIcon} />
                <Typography variant="h4" className={classes.primaryTitle}>
                  Ödeme Al
                </Typography>
                <Typography variant="body1" className={classes.primaryDescription}>
                  Hızlı ve güvenli ödeme işlemi başlatın
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Cards below the payment section */}
        <Box className={classes.statsContainer}>
          <Typography variant="h4" className={classes.sectionTitle}>
            Hızlı İşlemler
          </Typography>

          <Grid container spacing={3}>
            {getActionCards().map((card, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`action-card-${index}`}>
                <Card className={classes.actionCard} onClick={card.onClick}>
                  <CardContent className={classes.actionCardContent}>
                    {card.icon}
                    <Typography variant="h6" className={classes.actionTitle}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" className={classes.actionDescription}>
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box className={classes.statsContainer}>
          <Typography variant="h4" className={classes.sectionTitle}>
            İstatistikler
          </Typography>

          <Box className={classes.dateRangeContainer}>
            <DateRangeSelectRow selectionRange={selectionRange} onSelectionRange={setSelectionRange} />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                icon={<MonetizationOnOutlined />}
                title="Toplam Alınan Brüt Ödeme"
                text={formatTRYSetting.format(statistics?.totalGrossPaymentAmount ?? 0)}
                backgroundColor="#6c9c28"
              />
            </Grid>
            {userInfo.isAdmin && (            
              <Grid item xs={12} sm={6} md={4}>
                <InfoCard
                  icon={<TrendingUp />}
                  title="Karlılık"
                  text={formatTRYSetting.format(statistics?.profitability ?? 0)}
                  backgroundColor="#2563eb"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                icon={<MonetizationOnOutlined />}
                title="Ops Onay Bekleyen İşlem Tutarı"
                text={formatTRYSetting.format(statistics?.totalWaitingPaymentAmount ?? 0)}
                backgroundColor="#d97706"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                icon={<SendIcon />}
                title="Para Transferi Bekleyen İşlem Tutarı"
                text={formatTRYSetting.format(statistics?.totalTransferWaitingPaymentAmount ?? 0)}
                backgroundColor="#8b5cf6"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoCard
                icon={<AccountBalance />}
                title="Tamamlanan İşlem Tutarı"
                text={formatTRYSetting.format(statistics?.totalNetAmount ?? 0)}
                backgroundColor="#059669"
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default HomePage;
